import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { setPageTitle, setPageActions } from 'actions/header';
import { Box, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { connect } from 'react-redux';
import { getForm } from 'actions/forms';
import { getMyOwnedMentorGroups } from 'actions/groups';
import { getLeaveRequest } from 'actions/leaveRequests';
import { updateFormInstance, createFormInstance, getFormTemplatesByType } from 'actions/templates';
import { updateAbsenceLeaveRequest } from 'actions/absence';
import { Button, translate, Spinner } from '@haldor/ui';
import Moment from 'moment';
import LeaveRequest from '_class/LeaveRequest';
import User from '_class/User';
import DisplayForm from 'containers/FormTemplate/Display/DisplayForm';

// Constants
const DECISION_COLORS = {
	approved: { badge: '#4BAF4D', background: '#F2FEF7', color: '#ffffff' },
	rejected: { badge: '#DF2D2E', background: '#FDEFEE', color: '#ffffff' },
	withdrawn: { badge: '#9E9E9E', background: '#f5f5f5', color: '#ffffff' },
	default: { badge: '#ffffff', background: '#f5f5f5', color: 'text.primary', border: '1px solid #d0d0d0' }
};

const FIELD_TYPES = {
	NOTE: 'FIELDS_NOTE_INPUT_FIELD'
};

const FORM_TYPES = {
	LEAVE_RESPONSE: 'LEAVE_RESPONSE'
};

const STATUS = {
	APPROVED: 'APPROVED',
	REJECTED: 'REJECTED',
	REVOKED: 'REVOKED',
	SUBMITTED: 'SUBMITTED'
};

// Helper Functions
const formatDate = (date) => new Date(date).toISOString().split('T')[0];

const calculateDateRange = (startDate, endDate) => {
	const start = new Date(startDate);
	const end = new Date(endDate);
	const diffTime = Math.abs(end - start);
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
	return { start, end, diffDays };
};

const formatNoteTimestamp = (created, user) => {
	const timestamp = Moment.utc(created).local().format('YYYY-MM-DD HH:mm');
	if (!user) return timestamp;

	const firstName = user.firstName || '';
	const lastName = user.lastName || '';
	const fullName = `${firstName}${lastName ? ` ${lastName}` : ''}`.trim();
	return `${timestamp} - ${fullName}`;
};

const getDecisionColors = (decision) => {
	const normalized = (decision || '').toString().toLowerCase();
	if (normalized.includes('approved')) return DECISION_COLORS.approved;
	if (normalized.includes('rejected')) return DECISION_COLORS.rejected;
	if (normalized.includes('withdrawn')) return DECISION_COLORS.withdrawn;
	return DECISION_COLORS.default;
};

const filterNonNoteFields = (fields) => {
	return fields.filter(f => f.type !== FIELD_TYPES.NOTE);
};

const filterNoteFields = (fields) => {
	return fields.filter(f => f.type === FIELD_TYPES.NOTE);
};

const isLeaveResponseInstance = (instance) => {
	return instance.formTemplate?.type === FORM_TYPES.LEAVE_RESPONSE ||
		instance.formTemplate?.name === FORM_TYPES.LEAVE_RESPONSE;
};

function LeaveRequestView({ translate }) {
	const { id } = useParams();
	const location = useLocation();
	const dispatch = useDispatch();

	// Access passed data from navigation (if available)
	const passedLeaveRequest = location.state?.leaveRequest;

	// Redux state
	const { currentForm, loading } = useSelector(state => state.Forms);
	const currentLeaveRequest = useSelector(state => state.LeaveRequests.current);
	const currentUser = useSelector(state => state.user.currentUser);
	const templatesByType = useSelector(state => state.Templates?.templatesByType || {});

	// Local state
	const [mentorGroups, setMentorGroups] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [revoking, setRevoking] = useState(false);
	const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
	const [leaveResponseTemplate, setLeaveResponseTemplate] = useState(null);

	// Refs
	const lastFetchedFormIdRef = useRef(null);

	// Memoized values
	const user = useMemo(() =>
		currentUser ? new User(currentUser) : null,
		[currentUser]
	);

	const application = useMemo(() => {
		if (!currentForm || !currentLeaveRequest) return null;

		const formId = currentLeaveRequest.formId;
		if (!formId) return null;

		const isMatchingForm = currentForm.id === parseInt(formId) || currentForm.id == formId;
		if (isMatchingForm) {

			var application = new LeaveRequest(currentForm, mentorGroups, currentLeaveRequest);
			application.mentorGroups = passedLeaveRequest && passedLeaveRequest.mentorGroups && passedLeaveRequest.mentorGroups.length > 0 ? passedLeaveRequest.mentorGroups : application.mentorGroups; // Ensure mentor groups are included in the application data
			return application;
		}

		return null;
	}, [currentForm, mentorGroups, currentLeaveRequest]);

	const { directDecisionInstance, indirectDecisionInstance } = useMemo(() => {
		const hasRequiredData = application && user &&
			application.id === parseInt(id) &&
			currentForm?.formInstances;

		if (!hasRequiredData) {
			return { directDecisionInstance: null, indirectDecisionInstance: null };
		}

		return application.getDecisionInstancesForUser(currentForm.formInstances, user);
	}, [currentForm, user, id, application]);

	const decisionInstance = directDecisionInstance || indirectDecisionInstance;

	useEffect(() => {
		if (decisionInstance) {
			setLeaveResponseTemplate(null);
			return;
		}

		const template = templatesByType?.[FORM_TYPES.LEAVE_RESPONSE];
		setLeaveResponseTemplate(template);

		if (!template) {
			dispatch(getFormTemplatesByType(FORM_TYPES.LEAVE_RESPONSE));
		}
	}, [decisionInstance, templatesByType, dispatch]);

	// The most recently submitted LEAVE_RESPONSE from any decision maker.
	// Used to pre-fill the dialog when the current user has no personal decision instance yet.
	const lastDecisionInstance = useMemo(() => {
		if (!currentForm?.formInstances) return null;

		console.log('All form instances for decision lookup:', currentForm.formInstances);
		const submitted = currentForm.formInstances.filter(
			instance => isLeaveResponseInstance(instance) && instance.status === STATUS.SUBMITTED
		);

		if (submitted.length === 0) return null;

		console.log(submitted.sort((a, b) => new Date(b.edited || 0) - new Date(a.edited || 0)));
		// Return the most recently submitted decision instance regardless of who made it
		return submitted.sort((a, b) => new Date(b.edited || 0) - new Date(a.edited || 0))[0];
	}, [currentForm]);

	// Convert fieldResults to initialValues format for the decision form.
	// Prefers the current user's own instance; falls back to the last known decision
	// so the dialog is pre-filled even when someone else previously submitted a decision.
	const formInitialValues = useMemo(() => {
		const sourceInstance = lastDecisionInstance || directDecisionInstance;
		if (!sourceInstance?.fieldResults) return [];

		const decisionFields = filterNonNoteFields(sourceInstance.formTemplate?.fields || []);
		const decisionFieldNames = new Set(decisionFields.map(f => f.name));

		return sourceInstance.fieldResults.filter(
			result => decisionFieldNames.has(result.fieldName)
		);
	}, [directDecisionInstance, lastDecisionInstance]);

	const allNotes = useMemo(() => {
		if (!currentForm?.formInstances) return [];

		const leaveResponseInstances = currentForm.formInstances.filter(isLeaveResponseInstance);
		const notes = [];

		leaveResponseInstances.forEach(instance => {
			if (!instance.fieldResults) return;

			const noteFields = filterNoteFields(instance.formTemplate?.fields || []);
			const noteFieldNames = new Set(noteFields.map(f => f.name));

			const instanceNotes = instance.fieldResults.filter(result =>
				noteFieldNames.has(result.fieldName) && result.value?.trim()
			);

			notes.push(...instanceNotes);
		});

		notes.sort((a, b) => new Date(a.created || 0) - new Date(b.created || 0));
		return notes;
	}, [currentForm]);

	const refreshForm = useCallback(() => {
		if (!currentLeaveRequest?.formId) return Promise.resolve();
		return dispatch(getForm(currentLeaveRequest.formId));
	}, [dispatch, currentLeaveRequest]);

	const refreshLeaveRequest = useCallback((requestId) => {
		if (!requestId) return Promise.resolve();
		return dispatch(getLeaveRequest(requestId));
	}, [dispatch]);

	const refreshLeaveRequestWithForm = useCallback(async (requestId) => {
		if (!requestId) return;

		await refreshLeaveRequest(requestId);
		await refreshForm();
	}, [refreshLeaveRequest, refreshForm]);

	useEffect(() => {
		if (!user) return;

		refreshLeaveRequest(id);

		const fetchMentorGroups = async () => {
			const groups = await dispatch(getMyOwnedMentorGroups());
			setMentorGroups(Array.isArray(groups) ? groups : []);
		};

		fetchMentorGroups();
	}, [id, user, dispatch, refreshLeaveRequest]);

	useEffect(() => {
		if (!currentLeaveRequest) return;

		const formId = currentLeaveRequest.formId;
		if (!formId || lastFetchedFormIdRef.current === String(formId)) return;

		lastFetchedFormIdRef.current = String(formId);
		dispatch(getForm(formId)).catch(() => {
			lastFetchedFormIdRef.current = null;
		});
	}, [dispatch, currentLeaveRequest]);

	useEffect(() => {
		const title = application
			? (() => {
				const student = application.student;
				const name = student
					? `${student.firstName || ''} ${student.lastName || ''}`.trim()
					: `#${application.id}`;
				return `${translate('Leave request for')} ${name}`;
			})()
			: translate('Leave request');

		dispatch(setPageTitle(title));
		dispatch(setPageActions([]));
	}, [application, dispatch]);

	if (loading || !application) {
		return (
			<Box sx={{ p: 3 }}>
				<Spinner center />
			</Box>
		);
	}

	const handleToolButtonClick = () => {
		setModalOpen(true);
	};

	const handleRevokeApplication = () => {
		setConfirmRevokeOpen(true);
	};

	const handleConfirmRevoke = async () => {
		if (!application || !currentForm) return;

		try {
			setRevoking(true);
			await dispatch(updateAbsenceLeaveRequest(application.leaveRequest.id, currentForm.id, STATUS.REVOKED));
			await refreshLeaveRequestWithForm(id);
			setModalOpen(false);
			setConfirmRevokeOpen(false);
		} catch (error) {
			console.error('Failed to revoke application', error);
		} finally {
			setRevoking(false);
		}
	};

	// Permission checks
	const canHandleApplication = user && (user.isMentor() || user.isAdministrator() || user.havingPermission('LEAVES_EDIT_SCHOOL'));
	const canWithdrawApplication = user?.isStudent() &&
		user.isAuthoritative() &&
		user.getUserId() == application?.creator?.userId;
	const hasToolsAccess = user && (
		(user.isStudent() && user.isAuthoritative()) ||
		user.isMentor() ||
		user.isAdministrator() ||
		user.havingPermission('LEAVES_EDIT_SCHOOL')
	);
	const isDecided = application?.normalizedStatus === STATUS.REJECTED ||
		application?.normalizedStatus === STATUS.APPROVED;
	const canRevoke = !revoking &&
		application?.normalizedStatus !== STATUS.REJECTED &&
		(!application?.startDate || new Date(application.startDate) > new Date());

	return (
		<Box sx={{ pt: 1 }}>
			<Box sx={{ display: 'flex', gap: 4 }}>
				{/* Left Column - Decision and Information */}
				<Box sx={{ flex: 3 }}>
					{/* Decision Section */}
					{application.decision && isDecided && (
						<Paper
							sx={{
								p: 2,
								mb: 3,
								backgroundColor: getDecisionColors(application.status).background
							}}
						>
							<Typography variant="h5" gutterBottom><strong>{translate('Decision')}</strong></Typography>
							<Typography variant="h6" gutterBottom><strong>{translate('Status')}</strong></Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
								<Chip
									label={translate(application.status)}
									sx={{
										backgroundColor: getDecisionColors(application.status).badge,
										color: getDecisionColors(application.status).color,
										borderRadius: '5px'
									}}
								/>
								{application.decisionBy && (
									<Typography variant="body2">
										<strong>{application.decisionBy.firstName} {application.decisionBy.lastName}</strong>
										{application.decisionDate && (
											<> - {new Date(application.decisionDate).toISOString().split('T')[0]}</>
										)}
									</Typography>
								)}
							</Box>
							{application.decisionComment && (
								<>
									<Typography variant="h6" gutterBottom><strong>{translate('Decision reasoning')}</strong></Typography>
									<Typography variant="body2" sx={{ mb: 2 }}>{application.decisionComment}</Typography>
								</>
							)}
						</Paper>
					)}

					{/* Information Section */}
					<Paper sx={{ p: 2 }}>
						<Typography variant="h5" gutterBottom><strong>{translate('Application')}</strong></Typography>

						{!isDecided && (
							<div>
								<Typography variant="h6" gutterBottom><strong>{translate('Status')}</strong></Typography>
								<Box sx={{ mb: 2 }}>
									<Chip
										label={translate(application.status)}
										sx={{
											backgroundColor: getDecisionColors(application.status).badge,
											color: getDecisionColors(application.status).color,
											borderRadius: '5px',
											...(getDecisionColors(application.status).border && { border: getDecisionColors(application.status).border })
										}}
									/>
								</Box>
							</div>
						)}

						<Typography variant="h6" gutterBottom><strong>{translate('Application period')}</strong></Typography>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{application.startDate && application.endDate
								? (() => {
									const { start, end, diffDays } = calculateDateRange(application.startDate, application.endDate);
									return `${formatDate(start)} - ${formatDate(end)} (${diffDays} ${translate('days')})`;
								})()
								: '—'}
						</Typography>
						<Typography variant="h6" gutterBottom><strong>{translate('Reason')}</strong></Typography>
						<Typography variant="body2" sx={{ mb: 2 }}>{application.reason || '—'}</Typography>
						<Typography variant="h6" gutterBottom><strong>{translate('Application sent')}</strong></Typography>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{application.receivedDate ? formatDate(application.receivedDate) : '—'}
							{application.creator && `, ${application.creator.firstName} ${application.creator.lastName}`}
						</Typography>
						<Typography variant="h6" gutterBottom><strong>{translate('Student mentor groups')}</strong></Typography>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{application.mentorGroups && application.mentorGroups.length > 0
								? application.mentorGroups.map(group => group.title).join(', ')
								: '—'}
						</Typography>
					</Paper>

					{canHandleApplication && (<>
						<Paper sx={{ p: 2, mt: 3 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
								<Typography variant="h5">
									<strong>{translate('Notes')}</strong>
								</Typography>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
								<InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 0.3 }}>
									{translate('Notes are only visible to authorized personnel.')}
								</Typography>
							</Box>

							{/* Input form for new notes */}
							<DisplayForm
								fields={
									filterNoteFields(
										decisionInstance?.formTemplate?.fields ||
										leaveResponseTemplate?.fields ||
										[]
									)
								}
								initialValues={[]}
								hideSaveButton={true}
								onSuccess={async () => {
									try {
										await refreshLeaveRequestWithForm(id);
									} catch (e) {
										// Non-critical: swallow refresh failure
									}
								}}
								onCancel={() => { }}
								onSubmit={(data) => {
									if (!user) {
										return Promise.reject(new Error('Missing required data'));
									}

									if (decisionInstance?.formTemplate) {
										return dispatch(updateFormInstance({
											...decisionInstance,
											fieldResults: data.fieldResults
										}));
									}

									if (leaveResponseTemplate && (user.isMentor() || user.isAdministrator() || user.havingPermission('LEAVES_EDIT_SCHOOL'))) {
										return dispatch(createFormInstance({
											formId: application?.leaveRequest.formId,
											formTemplateId: leaveResponseTemplate.id,
											fieldResults: data.fieldResults
										}));
									}

									return Promise.reject(new Error('No permission to add notes'));
								}}
							/>

							{/* Display existing notes */}
							{allNotes.length > 0 ? (
								<Box sx={{ mb: 3 }}>
									{allNotes.map((note, index) => (
										<Box
											key={`${note.instanceId}-${note.id || index}`}
											sx={{
												mb: 1,
											}}
										>
											<Typography variant="body2">
												{note.value}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{formatNoteTimestamp(note.created, note.user)}
											</Typography>
										</Box>
									))}
								</Box>
							) : null}
						</Paper></>)}
				</Box>

				{/* Right Column - Tools */}
				{hasToolsAccess && (
					<Box sx={{ flex: 1 }}>
						<Typography variant="h5" gutterBottom><strong>{translate('Tools')}</strong></Typography>

						{canHandleApplication && (
							<Box sx={{ mt: 2 }}>
								<Button onClick={handleToolButtonClick}>
									{translate('Handle Application')}
								</Button>
							</Box>
						)}

						{canWithdrawApplication && (
							<Box sx={{ mt: 2 }}>
								<Button
									type="secondary"
									onClick={handleRevokeApplication}
									disabled={!canRevoke}
								>
									{translate('Withdraw')}
								</Button>
							</Box>
						)}
					</Box>
				)}
			</Box>

			<Dialog open={modalOpen} maxWidth="sm" fullWidth>
				<DialogTitle>
					{translate('Send decision for')} {application?.student?.firstName} {application?.student?.lastName}
				</DialogTitle>
				<DialogContent>
					<DisplayForm
						fields={
							filterNonNoteFields(
								lastDecisionInstance?.formTemplate?.fields ||
								decisionInstance?.formTemplate?.fields ||
								leaveResponseTemplate?.fields ||
								[]
							)
						}
						initialValues={formInitialValues}
						onCancel={() => setModalOpen(false)}
						onSuccess={async () => {
							setModalOpen(false);
							try {
								await refreshLeaveRequestWithForm(id);
							} catch (e) { }
						}}
						onSubmit={(data) => {
							const fields = decisionInstance?.formTemplate?.fields ||
								leaveResponseTemplate?.fields ||
								[];

							const decisionFields = filterNonNoteFields(fields);
							const decisionFieldNames = new Set(decisionFields.map(f => f.name));

							const filteredFieldResults = data.fieldResults.filter(
								result => decisionFieldNames.has(result.fieldName)
							);

							const decisionValue = LeaveRequest.extractDecisionValue(filteredFieldResults, fields);
							const formStatus = LeaveRequest.mapDecisionToFormStatus(decisionValue);

							const submitDecision = async (instanceData) => {
								try {
									await dispatch(instanceData);
									await dispatch(updateAbsenceLeaveRequest(application?.leaveRequest.id, application?.leaveRequest.formId, formStatus));
								} catch (error) {
									console.error('Error submitting decision:', error);
									throw error;
								}
							};

							if (decisionInstance) {
								return submitDecision(updateFormInstance({
									...decisionInstance,
									fieldResults: filteredFieldResults,
									status: STATUS.SUBMITTED
								}));
							}

							if (leaveResponseTemplate && canHandleApplication) {
								return submitDecision(createFormInstance({
									formId: application?.leaveRequest.formId,
									formTemplateId: leaveResponseTemplate.id,
									fieldResults: filteredFieldResults,
									status: STATUS.SUBMITTED
								}));
							}

							return Promise.reject(new Error('No permission to create decision instance'));
						}}
					/>
				</DialogContent>
			</Dialog>

			<Dialog
				open={confirmRevokeOpen}
				onClose={() => { if (!revoking) setConfirmRevokeOpen(false); }}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{translate('Are you sure?')}</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						{translate('Withdrawal of the application cannot be undone. Do you want to continue')}
					</Typography>
					{revoking && (
						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
							<Spinner center />
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button type="secondary" disabled={revoking} onClick={() => setConfirmRevokeOpen(false)}>
						{translate('Cancel')}
					</Button>
					<Button onClick={handleConfirmRevoke} disabled={revoking}>
						{translate('Revoke')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box >
	);
}

export default connect(
	(state) => ({
		translate: translate(state.Languages.translations),
	})
)(LeaveRequestView);